'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useSignIn } from '@clerk/nextjs';

import Link from 'next/link';
import Image from 'next/image';
import { ArrowLeft, Mail } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';

export default function ForgotPasswordForm() {
    const router = useRouter();
    const { isLoaded, signIn } = useSignIn();
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsSubmitted(false);

        if (!isLoaded) {
            return;
        }

        setIsLoading(true);

        try {
            await signIn.create({
                strategy: 'reset_password_email_code',
                identifier: email,
            });

            setIsSubmitted(true);
            await new Promise((resolve) => setTimeout(resolve, 2000));
            router.push(`/reset-password?email=${encodeURIComponent(email)}`);
        } catch (err: unknown) {
            // Message volontairement générique pour éviter toute fuite d'information.
            setError('Impossible d envoyer le mail pour le moment. Veuillez reessayer.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="bg-linear-to-br from-blue-50 to-gray-100 flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                <div className="mb-6">
                    <Link href="/login" className="flex items-center gap-2 text-gray-700">
                        <ArrowLeft className="h-4 w-4" />
                        Retour à la connexion
                    </Link>
                </div>

                <div className="bg-white rounded-2xl shadow-xl p-8">
                    <div className="flex justify-center mb-6">
                        <Image src="/img/Fluidexpert-full-dark-250px.png" alt="Fluidexpert Logo" width={250} height={100} className="object-contain h-auto w-auto" />
                    </div>

                    <div className="mb-8 text-center">
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Mot de passe oublié</h2>
                        <p className="text-sm text-gray-600">
                            Page fictive de réinitialisation du mot de passe.
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="votre.email@exemple.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="pl-10"
                                    required
                                />
                            </div>
                        </div>

                        <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={isLoading}>
                            {isLoading ? 'Envoi en cours...' : 'Envoyer le code de réinitialisation'}
                        </Button>

                        {isSubmitted && (
                            <p className="text-sm text-green-700 text-center">
                                Si cet email existe, un code de réinitialisation a été envoyé.
                            </p>
                        )}

                        {error && (
                            <p className="text-sm text-red-600 text-center">
                                {error}
                            </p>
                        )}
                    </form>
                </div>
            </div>
        </div>
    );
}
